import {
  SalesforceActions,
  RLProjectFieldMapping,
} from 'module/api/integrations/salesforce/types';
import { Loading } from 'module/ui/components/atoms/loading';
import { useParams } from '@rocketlane/rocket-router';
import { useSalesForceActionFieldsQuery } from 'queries/salesforce';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { BlockList } from 'components/organisms/signal/detail/block-list/list';
import { ActionDrawer } from '../../create/action-drawer';
import { useAutomationContext } from '../../create/automation-context';
import { getFields } from '../../create/blocks/shared/block-utils';
import { RenderBlockDetails } from '../../create/blocks/shared/render-block-details';

const salesForceType = SalesforceActions.Account;

export const AutomationAccountEditBlock: FC = () => {
  const { integrationId } = useParams<{
    integrationId: string;
    automationId: string;
  }>();

  const { automationDetails, setAutomationDetails } = useAutomationContext();

  const [actionDrawerVisible, setActionDrawerVisible] = useState(false);

  const fieldsKey = getFields(salesForceType);

  const {
    salesforceObject: selectedSalesforceObject,
    salesForceType: selectedSalesForceType,
  } = automationDetails || {};

  const { value } = selectedSalesforceObject || {};

  // For project automation flow, pass the companyObject placeholder
  const triggerObjectParentPlaceholder =
    selectedSalesForceType === SalesforceActions.Project
      ? automationDetails?.rocketlaneFields?.companyObject?.valueStr
      : undefined;

  const {
    data: accountFields,
    status,
    isFetching,
  } = useSalesForceActionFieldsQuery(
    parseInt(integrationId || '0'),
    String(value),
    salesForceType,
    triggerObjectParentPlaceholder,
    {
      enabled: !!value,
    },
  );
  const isFieldsLoading = status === 'pending' || isFetching;

  useEffect(() => {
    // ✅ If actions are cleared, ensure accountFields are also cleared
    if (
      accountFields &&
      accountFields.length > 0 &&
      !automationDetails?.actions
    ) {
      setAutomationDetails({
        [fieldsKey]: undefined,
      });

      return;
    }

    if (
      accountFields &&
      accountFields.length > 0 &&
      automationDetails?.actions?.[0]?.inputs
    ) {
      const isProjectAutomation =
        automationDetails.salesForceType === SalesforceActions.Project;
      const inputs = isProjectAutomation
        ? automationDetails.actions[0].dependentActions?.[0]?.inputs
        : automationDetails.actions[0].inputs;

      if (!inputs) {
        return;
      }

      const selectedData = Object.keys(inputs).reduce((acc, each) => {
        const currentField = accountFields.find((r) => r.fieldName === each);

        if (currentField) {
          const val = inputs[each];

          const rlFieldValue = currentField?.metaData?.options?.find(
            (o) =>
              String(o.valueStr ?? (o as any).value ?? '') ===
              String(val ?? ''),
          );

          acc[each] = {
            label: rlFieldValue?.label ?? (val as string),
            valueStr: String(
              rlFieldValue?.valueStr ?? (rlFieldValue as any)?.value ?? val,
            ),
            valueMeta: rlFieldValue?.valueMeta,
          };

          return acc;
        }

        return acc;
      }, {});

      if (Object.keys(selectedData).length > 0) {
        setAutomationDetails({
          [fieldsKey]: selectedData,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountFields]);

  const onSave = (updatedFields: RLProjectFieldMapping) => {
    const fields = Object.keys(updatedFields).reduce((acc, key) => {
      acc[key] =
        key !== 'teamMembers'
          ? updatedFields[key] != null
            ? {
                ...updatedFields[key],
                label:
                  updatedFields[key]?.valueMeta?.fieldLabel ??
                  updatedFields[key]?.label,
              }
            : undefined
          : updatedFields[key];

      return acc;
    }, {});

    setAutomationDetails({
      [fieldsKey]: fields,
    });
  };

  return (
    <>
      <BlockList.Item
        disabled={!value || isFieldsLoading}
        onClick={() => setActionDrawerVisible(true)}
      >
        <BlockList.Item.Label data-required="true">
          <BlockList.Item.Label.Text>
            <FormattedMessage
              id={`settings.integrations.create.automation.${salesForceType?.toLowerCase()}.label`}
            />
          </BlockList.Item.Label.Text>
        </BlockList.Item.Label>
        <BlockList.Item.Content>
          {isFieldsLoading ? (
            <Loading />
          ) : (
            <RenderBlockDetails
              actions={automationDetails?.[fieldsKey] ?? {}}
              fields={accountFields ?? []}
            />
          )}
        </BlockList.Item.Content>
      </BlockList.Item>
      <ActionDrawer
        label="account"
        fields={accountFields ?? []}
        fieldsStatus={isFieldsLoading ? 'pending' : 'success'}
        visible={actionDrawerVisible}
        value={automationDetails?.[fieldsKey] ?? undefined}
        handleModalVisibility={setActionDrawerVisible}
        onSave={onSave}
        onClose={() => setActionDrawerVisible(false)}
      />
    </>
  );
};
